'use client';

import { useState } from 'react';
import { Dialog } from '@/components/ui/Dialog';
import { useLibraryStore } from '@/stores/library-store';

export function AddLibraryDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const addLibrary = useLibraryStore((s) => s.addLibrary);
  const [name, setName] = useState('');
  const [rootUrl, setRootUrl] = useState('');
  const [useAuth, setUseAuth] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<'success' | 'error' | null>(null);
  const [testError, setTestError] = useState('');

  const reset = () => {
    setName('');
    setRootUrl('');
    setUseAuth(false);
    setUsername('');
    setPassword('');
    setTesting(false);
    setTestResult(null);
    setTestError('');
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    setTestError('');
    try {
      const params = new URLSearchParams({ url: rootUrl });
      if (useAuth && username && password) {
        params.set('username', username);
        params.set('password', password);
      }
      const res = await fetch(`/api/opds/feed?${params}`);
      if (res.ok) {
        setTestResult('success');
      } else {
        const data = await res.json();
        setTestResult('error');
        setTestError(data.error || `HTTP ${res.status}`);
      }
    } catch (e) {
      setTestResult('error');
      setTestError(e instanceof Error ? e.message : 'Connection failed');
    } finally {
      setTesting(false);
    }
  };

  const handleSubmit = () => {
    if (!rootUrl.trim()) return;
    addLibrary({
      name: name.trim() || 'My Library',
      rootUrl: rootUrl.trim(),
      auth: useAuth ? { type: 'basic', username, password } : undefined,
    });
    reset();
    onClose();
  };

  return (
    <Dialog open={open} onClose={() => { reset(); onClose(); }} title="Add OPDS Library">
      <div className="space-y-4">
        <div>
          <label className="mb-1 block text-sm font-medium text-neutral-700">
            Library Name
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="My Calibre Library"
            className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-neutral-700">
            OPDS Catalog URL <span className="text-red-500">*</span>
          </label>
          <input
            type="url"
            value={rootUrl}
            onChange={(e) => setRootUrl(e.target.value)}
            placeholder="https://example.com/opds"
            required
            className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={useAuth}
              onChange={(e) => setUseAuth(e.target.checked)}
              className="rounded border-neutral-300"
            />
            <span className="text-sm text-neutral-700">Requires authentication</span>
          </label>
        </div>

        {useAuth && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-sm font-medium text-neutral-700">Username</label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-neutral-700">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
          </div>
        )}

        {testResult === 'success' && (
          <div className="rounded-lg bg-green-50 px-3 py-2 text-sm text-green-700">
            Connection successful
          </div>
        )}
        {testResult === 'error' && (
          <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
            Connection failed: {testError}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={handleTest}
            disabled={!rootUrl || testing}
            className="rounded-lg border border-neutral-300 px-4 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50 disabled:opacity-50"
          >
            {testing ? 'Testing...' : 'Test Connection'}
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!rootUrl}
            className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            Add Library
          </button>
        </div>
      </div>
    </Dialog>
  );
}
